"use client"

import React, { useMemo, useState } from "react"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { SelectGroup, SelectItem, SelectLabel } from "@/components/ui/select"
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react"
import { groupEntityTypes } from "@/lib/entity-groups"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"
import type { AtsEntityTypeInfo } from "@/types/ats-explorer-api"

/** Below this many entries a catalog needs no search box */
const SEARCH_THRESHOLD = 8

export interface EntityBrowserProps {
  entities: AtsEntityTypeInfo[]
  selectedName: string | null
  onSelect: (entity: AtsEntityTypeInfo) => void
  isLoading?: boolean
}

/**
 * Grouped, searchable entity list for the explorer's side panel. Small catalogs
 * (Loxo, JobAdder) render flat; Bullhorn tenants expose 100+ entities, so groups
 * collapse except the first (core) one until the user searches or opens them.
 */
export function EntityBrowser({ entities, selectedName, onSelect, isLoading = false }: EntityBrowserProps) {
  const { t, n } = useTranslation()
  const [query, setQuery] = useState("")
  const [openGroups, setOpenGroups] = useState<Set<string> | null>(null)

  const groups = useMemo(() => groupEntityTypes(entities, query), [entities, query])
  const isGrouped = groups.length > 1
  const searching = query.trim() !== ""
  // The vendor's group name, or the label for the entities it leaves ungrouped
  const groupLabel = (name: string | null) => name ?? t("explorer.entityBrowser.ungrouped")

  const isOpen = (name: string, index: number) => searching || !isGrouped || (openGroups ? openGroups.has(name) : index === 0)

  const toggleGroup = (name: string, index: number) => {
    setOpenGroups((current) => {
      const next = new Set(current ?? (groups[0] ? [groupLabel(groups[0].name)] : []))
      if (isOpen(name, index)) {
        next.delete(name)
      } else {
        next.add(name)
      }
      return next
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center p-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> {t("common.state.loading")}
      </div>
    )
  }

  return (
    <div className="space-y-1">
      {entities.length > SEARCH_THRESHOLD && (
        <Input
          className="h-8 text-xs mb-2"
          placeholder={t("explorer.entityBrowser.searchEntities", { count: n(entities.length) })}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      )}
      {groups.length === 0 && <p className="px-3 py-2 text-xs text-muted-foreground">{t("explorer.entityBrowser.noMatches")}</p>}
      {groups.map((group, index) => (
        <div key={groupLabel(group.name)}>
          {isGrouped && (
            <button
              type="button"
              className="w-full flex items-center gap-1 px-2 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide hover:text-foreground"
              onClick={() => toggleGroup(groupLabel(group.name), index)}
            >
              {isOpen(groupLabel(group.name), index) ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              {groupLabel(group.name)}
              <span className="ml-auto font-normal normal-case tracking-normal">{n(group.entities.length)}</span>
            </button>
          )}
          {isOpen(groupLabel(group.name), index) &&
            group.entities.map((entity) => (
              <EntityButton key={entity.name} entity={entity} selected={selectedName === entity.name} onSelect={onSelect} />
            ))}
        </div>
      ))}
    </div>
  )
}

interface EntityButtonProps {
  entity: AtsEntityTypeInfo
  selected: boolean
  onSelect: (entity: AtsEntityTypeInfo) => void
}

function EntityButton({ entity, selected, onSelect }: EntityButtonProps) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      onClick={() => onSelect(entity)}
      className={`w-full text-left rounded-md px-3 py-2 text-sm mb-1 hover:bg-accent ${selected ? "bg-accent font-medium" : ""}`}
    >
      <div>{entity.displayName}</div>
      <div className="text-xs text-muted-foreground flex gap-1 mt-0.5">
        {entity.supportsKeywordSearch && <Badge variant="outline" className="text-[10px] px-1 py-0">{t("explorer.entityBrowser.badges.search")}</Badge>}
        {entity.supportsFilters && <Badge variant="outline" className="text-[10px] px-1 py-0">{t("explorer.entityBrowser.badges.filters")}</Badge>}
        {entity.supportsFieldSelection && <Badge variant="outline" className="text-[10px] px-1 py-0">{t("explorer.entityBrowser.badges.fields")}</Badge>}
      </div>
    </button>
  )
}

export interface ReferenceTypeBrowserProps {
  referenceTypes: string[]
  selectedType: string | null
  onSelect: (referenceType: string) => void
}

/** Searchable list of reference data set names (Bullhorn tenants expose 25+) */
export function ReferenceTypeBrowser({ referenceTypes, selectedType, onSelect }: ReferenceTypeBrowserProps) {
  const { t, n } = useTranslation()
  const [query, setQuery] = useState("")
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    return q ? referenceTypes.filter((type) => type.toLowerCase().includes(q)) : referenceTypes
  }, [referenceTypes, query])

  return (
    <div>
      {referenceTypes.length > SEARCH_THRESHOLD && (
        <Input
          className="h-8 text-xs mb-2"
          placeholder={t("explorer.entityBrowser.searchReference", { count: n(referenceTypes.length) })}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      )}
      {visible.map((referenceType) => (
        <button
          key={referenceType}
          type="button"
          onClick={() => onSelect(referenceType)}
          className={`w-full text-left rounded-md px-3 py-1.5 text-sm mb-0.5 hover:bg-accent ${selectedType === referenceType ? "bg-accent font-medium" : ""}`}
        >
          {referenceType}
        </button>
      ))}
    </div>
  )
}

/** Select items grouped the same way as the browser, for the wizard's dropdowns */
export function GroupedEntitySelectItems({ entities }: { entities: AtsEntityTypeInfo[] }) {
  const { t } = useTranslation()
  const groups = useMemo(() => groupEntityTypes(entities), [entities])

  if (groups.length <= 1) {
    return (
      <>
        {entities.map((entity) => (
          <SelectItem key={entity.name} value={entity.name}>
            {entity.displayName}
          </SelectItem>
        ))}
      </>
    )
  }

  return (
    <>
      {groups.map((group) => (
        <SelectGroup key={group.name ?? "ungrouped"}>
          <SelectLabel>{group.name ?? t("explorer.entityBrowser.ungrouped")}</SelectLabel>
          {group.entities.map((entity) => (
            <SelectItem key={entity.name} value={entity.name}>
              {entity.displayName}
            </SelectItem>
          ))}
        </SelectGroup>
      ))}
    </>
  )
}
